/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Link } from 'react-router-dom';
import { Loader, RefreshCw, Building2 } from 'lucide-react';

interface OrganizationInfo {
  id: string;
  name: string;
}

interface ProductListHeaderProps {
  filteredCount: number;
  totalCount: number;
  isLoading: boolean;
  onRefresh: () => void;
  organization?: OrganizationInfo;
  repoContext?: {
    repoUrl?: string;
    repoPath?: string;
  };
}

export function ProductListHeader({
  filteredCount,
  totalCount,
  isLoading,
  onRefresh,
  organization,
  repoContext
}: ProductListHeaderProps) {
  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Organization and Product Count Row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: organization ? '8px' : '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0 }}>Products ({filteredCount} of {totalCount})</h3>
          {organization && (
            <Link 
              to="/settings?tab=organization"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                padding: '4px 12px',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                border: '1px solid #bae6fd',
                borderRadius: '16px',
                color: '#0369a1',
                fontSize: '13px',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)';
                e.currentTarget.style.borderColor = '#7dd3fc';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)';
                e.currentTarget.style.borderColor = '#bae6fd';
              }}
              title="Click to change organization"
            >
              <Building2 size={14} />
              {organization.name}
            </Link>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="btn btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              padding: '8px 16px'
            }}
          >
            {isLoading ? (
              <>
                <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Loading...
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {/* Active Repo Context Row */}
      {repoContext?.repoUrl && (
        <div style={{ fontSize: '12px', color: '#2563eb' }}>
          Managing data for <span style={{ fontFamily: 'monospace' }}>{repoContext.repoUrl}</span>
          {repoContext.repoPath && (
            <span style={{ color: '#475569' }}> ({repoContext.repoPath})</span>
          )}
        </div>
      )}
    </div>
  );
}

